import type { OnInit } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import type { Observable } from 'rxjs';

import type { FormElement, Option } from '@common/types/form-element.type';
import type { FormButtonSettings } from '@components/sp-form-button-settings';
import type { FormInputSettings } from '@components/sp-form-input-settings';
import type { ChatBotData } from '@web/widgets/chat-bot/type/chat-bot-widget.type';
import type { FormWidgetData } from '@web/widgets/form/type/form-widget.type';
import type { WebLegacyWidgetEntity } from '@web/types';
import type { FieldTree } from '@angular/forms/signals';
import type { ExtendedPositionLegacy } from '@sites/entity-core';
import type { FormWidgetForm } from '@sites/entity-signal-form';

import { ChangeDetectionStrategy, Component, ElementRef, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { ReactiveFormsModule } from '@angular/forms';
import { CountryISO, NgxIntlTelInputModule, PhoneNumberFormat, SearchCountryField } from '@moddi3/ngx-intl-tel-input';
import { NgSelectModule } from '@ng-select/ng-select';

import {
    EButtonAdditionalEffectsCondition,
    EButtonAdditionalHoverEffectsTypes,
    EButtonAdditionalStaticEffectsTypes,
    EButtonStyleType,
    EButtonType,
    EFormInputType,
    EFormView,
    ESize,
    ESocialNetworks,
    FormStyleEnum,
} from '@common/enums';
import * as typeGuards from '@common/type-guards/form-elements.type-guards';
import { socialIconTemplates } from '@common/icons/social-icons';
import { RemovePTags } from '@common/pipes/remove-p-tag.pipe';
import { CountryISOService } from '@common/services/REST/country-iso.http.service';

import { LayoutFacade } from '@web/store/layout/layout.facade';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';
import { getSocialNetworkIconSrc } from '@web/shared/components/web-social-networks/utils';

import { WebBaseViewSignalFormComponent } from '@web/widgets/common/abstract/web-base-view-signal-form.component';
import {
    BUTTON_SIZE_CLASS_MAP,
    BUTTON_TYPE_CLASS_MAP,
    DEFAULT_BUTTON_CLASS,
} from '@web/widgets/common/base/base-button/base-button.const';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';

import {
    getFormAlignCssVars,
    getFormButtonCssClasses,
    getFormButtonCssVars,
    getFormChatBotCssClasses,
    getFormChatBotCssVars,
    getFormChatBotWrapperCssClasses,
    getFormInputCssClasses,
    getFormInputCssVars,
    getFormRootCssClasses,
    getFormRootCssVars,
    getFormSizeCssVars,
} from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import type { FormCssVars } from '@sites/entity-core/src/widget/form/styles/form.css-vars';
import { SpIntlTelInputA11yDirective } from '@common/directives/intl-tel-input-a11y.directive';
import { FORM_ROOT_THEME_TOKENS, colorCss } from '@web/widgets/common/css-vars/css-vars.utils';
import { afterFormValueChange } from '@web/widgets/common/zero-size-sync/after-form-value-change';
import { NewZeroWidgetsService } from '@web/widgets/common/zero-size-sync/new-zero-widgets.service';

@Component({
    selector: 'web-form-view',
    templateUrl: './form-view.component.html',
    styleUrls: ['./form-view.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        RemovePTags,
        ReactiveFormsModule,
        NgxIntlTelInputModule,
        SpIntlTelInputA11yDirective,
        NgSelectModule,
    ],
})
export class FormViewComponent
    extends WebBaseViewSignalFormComponent<WebLegacyWidgetEntity<FormWidgetData>>
    implements OnInit
{
    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly newZeroWidgets: NewZeroWidgetsService = inject(NewZeroWidgetsService);
    private readonly countryISOService: CountryISOService = inject(CountryISOService);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public form!: FormWidgetForm;

    protected readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    protected readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    public readonly SearchCountryField = SearchCountryField;
    public readonly PhoneNumberFormat = PhoneNumberFormat;
    public readonly typeGuards = typeGuards;
    public readonly formInputType = EFormInputType;
    public readonly EButtonType = EButtonType;
    public readonly EFormView = EFormView;
    public CountryISO = CountryISO.Ukraine;
    public excludeCountries = [CountryISO.Russia];

    /** Typed as the app's `FormWidgetData`: the css-vars helpers take its enums, not the schema's literal unions. */
    protected readonly data = computed<FormWidgetData>(() => this.form.value().data as unknown as FormWidgetData);

    /**
     * True when the widget lives inside a zero block. Read through `value()`
     * — NOT `form.type()`: the `type` FieldTree child only materialises when
     * the key exists in the model, and default-variant entities omit it.
     */
    public readonly isZero = computed<boolean>(() => this.form?.value().type === 'zero');

    public ngOnInit(): void {
        this.form = this.signalFormService.getOrCreateScoped<FormWidgetForm>(this.widget().id, this.destroyRef);

        this.getCountryISO();

        if (this.isZero()) {
            this.setupZeroHeightSync();
        }
    }

    /**
     * Zero-block forms auto-grow with their content: the rendered root's
     * height is mirrored back into the extended-position grid so the zero
     * host's selection frame and boundary math track the real size (manual
     * height resize is disabled for FORM in the zero host) — the button
     * view's `setupZeroHeightSync` pattern.
     */
    private setupZeroHeightSync(): void {
        afterFormValueChange(
            this.injector,
            () => this.form._raw().value(),
            () => this.syncZeroHeightFromDom(),
            { measureInitial: this.newZeroWidgets.consume(this.widget().id) }
        );
    }

    private syncZeroHeightFromDom(): void {
        const root = this.elementRef.nativeElement.firstElementChild as HTMLElement | null;
        if (!root) return;

        const height = parseFloat(getComputedStyle(root).height);
        if (!Number.isFinite(height) || height <= 0) return;

        // The form's position accessor is union-typed (simple grid on default
        // placement) — this code path only runs for zero forms.
        const positionField = this.form.position as unknown as FieldTree<ExtendedPositionLegacy>;
        const position = positionField().value();

        if (this.isDeviceMobileSig()) {
            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            return;
        }

        if (this.isDeviceTabletSig()) {
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            return;
        }

        const writeAll = () => {
            if (position.mobileHeight !== height) positionField.mobileHeight().value.set(height);
            if (position.tabletHeight !== height) positionField.tabletHeight().value.set(height);
            if (position.height !== height) positionField.height().value.set(height);
        };

        // Batch the three device writes into one store dispatch — unless an
        // outer transaction (settings panel editing) is already open: the
        // plugin doesn't support nesting, a nested `transact` would commit
        // the outer snapshot prematurely.
        if (this.form.inTransaction()) {
            writeAll();
        } else {
            this.form.transact(writeAll);
        }
    }

    public getButtonEffectClass(button: FormButtonSettings): string {
        let nameEffect: string = '';

        if (button.additionalEffects?.isActive) {
            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.hover) {
                switch (button.additionalEffects?.hoverEffects) {
                    case EButtonAdditionalHoverEffectsTypes.bounce:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.bounce;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.shake:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.shake;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.decrease:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.decrease;
                        break;
                    case EButtonAdditionalHoverEffectsTypes.increase:
                        nameEffect = EButtonAdditionalHoverEffectsTypes.increase;
                        break;
                    default:
                        break;
                }
            }

            if (button.additionalEffects?.effectCondition === EButtonAdditionalEffectsCondition.static) {
                switch (button.additionalEffects?.staticEffects) {
                    case EButtonAdditionalStaticEffectsTypes.shine:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.shine;
                        break;
                    case EButtonAdditionalStaticEffectsTypes.twinkle:
                        nameEffect = EButtonAdditionalStaticEffectsTypes.twinkle;
                        break;
                    default:
                        break;
                }
            }

            return 'button_effect_' + nameEffect;
        }

        return '';
    }

    public getButtonStyles(formButtonSettings: FormButtonSettings): string {
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[formButtonSettings.size ? formButtonSettings.size : ESize.M]} form-button ${getFormButtonCssClasses(formButtonSettings)}`;

        if (formButtonSettings.styleType === EButtonStyleType.custom) {
            return `${styles} ${this.getButtonEffectClass(formButtonSettings)}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[formButtonSettings.styleType]} ${this.getButtonEffectClass(
            formButtonSettings
        )}`;
    }

    // Icons are constant per markup string, but these methods are bound in
    // `@for` loops and re-run every change-detection tick. `bypassSecurityTrustHtml`
    // returns a fresh `SafeHtml` each call, which forces Angular to rewrite the
    // `[innerHTML]` DOM node every tick — cache by source so the reference is stable.
    private readonly sanitizedIconCache = new Map<string, SafeHtml>();

    public getIcon(icon: string): SafeHtml {
        return this.getSanitizedIcon(icon);
    }

    public getChatBotIcon(iconName: string): SafeHtml {
        return this.getSanitizedIcon(socialIconTemplates[iconName]);
    }

    private getSanitizedIcon(markup: string): SafeHtml {
        const cached = this.sanitizedIconCache.get(markup);
        if (cached !== undefined) {
            return cached;
        }

        const safe = this.sanitizer.bypassSecurityTrustHtml(markup);
        this.sanitizedIconCache.set(markup, safe);

        return safe;
    }

    public getSocialNetworkIconSrcByChatBot(chatBot: ChatBotData): string {
        const folderName: string = chatBot.type === EButtonType.button ? 'full-white' : chatBot.iconType;
        return getSocialNetworkIconSrc(folderName, chatBot.socialNetwork);
    }

    public getStyleList(chatbot): string {
        const chatBotsButtonSettings = this.data().chatBotsButtonSettings;
        const styles = `${DEFAULT_BUTTON_CLASS} ${BUTTON_SIZE_CLASS_MAP[chatBotsButtonSettings.buttonSize ? chatBotsButtonSettings.buttonSize : ESize.M]} form-chatbot-anchor ${getFormChatBotCssClasses(chatBotsButtonSettings)}`;

        if (chatBotsButtonSettings.styleType === EButtonStyleType.custom) {
            return styles;
        }

        if (chatBotsButtonSettings.styleType === EButtonStyleType.classic) {
            let chatBotBrandClass = '';

            switch (chatbot) {
                case ESocialNetworks.messenger:
                    chatBotBrandClass = 'chat-bot-messenger';
                    break;
                case ESocialNetworks.instagram:
                    chatBotBrandClass = 'chat-bot-instagram';
                    break;
                case ESocialNetworks.telegram:
                    chatBotBrandClass = 'chat-bot-telegram';
                    break;
                case ESocialNetworks.viber:
                    chatBotBrandClass = 'chat-bot-viber';
                    break;
                case ESocialNetworks.whatsapp:
                    chatBotBrandClass = 'chat-bot-whatsapp';
                    break;
                case ESocialNetworks.tiktok:
                    chatBotBrandClass = 'chat-bot-tiktok';
                    break;
            }

            return `${styles} ${BUTTON_TYPE_CLASS_MAP[EButtonStyleType.primary]} ${chatBotBrandClass}`;
        }

        return `${styles} ${BUTTON_TYPE_CLASS_MAP[chatBotsButtonSettings.styleType]}`;
    }

    public getSelected(options: Option[]) {
        return !options.some((el: Option) => el.selected);
    }

    private getCountryISO() {
        this.countryISOService.getCountryISO().subscribe((data) => {
            this.CountryISO = data.country_code.toLowerCase();
            this.changeDetectorRef.detectChanges();
        });
    }

    public getPlaceholder(options: Option[] = []): string {
        const arr = options.filter((el) => el.selected).map((el) => el.label);

        return arr.length ? arr.join(', ') : 'select_value';
    }

    public readonly rootFormWithPositionClasses = computed<string>(() =>
        getFormRootCssClasses(this.form.inputSettings().value())
    );

    /** Root layout + input theming + input-size + alignment variables (cascade to field/control descendants). */
    public readonly rootCssVars = computed<FormCssVars>(() => ({
        ...getFormRootCssVars(this.form.value(), FORM_ROOT_THEME_TOKENS),
        ...getFormInputCssVars(this.form.inputSettings().value(), colorCss),
        ...getFormSizeCssVars(this.form.inputSettings().value()),
        ...getFormAlignCssVars(this.form.inputSettings().value()),
    }));

    /** Full/bottom border gate, shared by every field and control (sizing is variable-driven). */
    public readonly fieldCssClasses = computed<string>(() => getFormInputCssClasses(this.form.inputSettings().value()));

    /** Submit-button variables — set on the wrapper, inherited by the anchor. */
    public readonly buttonCssVars = computed<FormCssVars>(() => {
        const { formButtonSettings, inputSettings } = this.data();
        return getFormButtonCssVars(formButtonSettings, inputSettings.formStyleType, colorCss, this.isZero());
    });

    public readonly buttonWrapperCssClasses = computed<string>(() => {
        return this.form.inputSettings().value().formStyleType === FormStyleEnum.HORIZONTAL
            ? 'form-horizontal'
            : 'form-vertical';
    });

    /** Chat-bot custom-button variables — set on the wrapper, inherited by anchors. */
    public readonly chatBotCssVars = computed<FormCssVars>(() =>
        getFormChatBotCssVars(this.data().chatBotsButtonSettings, colorCss)
    );

    public readonly chatBotWrapperCssClasses = computed<string>(() => {
        return getFormChatBotWrapperCssClasses(this.data().chatBotsButtonSettings, this.form.inputSettings().value());
    });
}
